/**
 * Анонс проекта.
 *
 * Показывается один раз каждому: и тому, кто только что вошёл, и тому, кто
 * уже был авторизован, когда анонс опубликовали. Признак живёт у пользователя,
 * а не в браузере, — иначе с каждого нового устройства он всплывал бы заново.
 *
 * Новичку анонс идёт следом за инструкцией, а не поверх неё: два окна разом
 * читает разве что тот, кто их и писал.
 *
 * Если за время отсутствия накопилось несколько, показывается последний, а
 * остальные отмечаются прочитанными вместе с ним. Очередь окон на входе — это
 * не «вы всё пропустили», а препятствие; кому интересно, найдёт их списком
 * в профиле.
 */
import { useTranslation } from "react-i18next";

import { useAnnouncements, useMarkAnnouncementSeen } from "../api/queries";
import { formatDate } from "../lib/format";
import { Dialog } from "./Dialog";

export function AnnouncementDialog({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  const unseen = useAnnouncements(true, enabled).data?.announcements ?? [];
  const markSeen = useMarkAnnouncementSeen();

  // Свежий сверху: сервер отдаёт их по дате публикации.
  const item = unseen[0];
  if (!item?.id) return null;

  const close = () => {
    for (const announcement of unseen) {
      if (announcement.id) markSeen.mutate(announcement.id);
    }
  };

  return (
    <Dialog title={item.title ?? t("announcements.title")} onClose={close}>
      {item.coverImageUrl && (
        <img
          src={item.coverImageUrl}
          alt=""
          className="mb-4 aspect-[16/9] w-full rounded-xl border border-ink-800 object-cover"
        />
      )}

      <p className="whitespace-pre-line text-sm leading-relaxed text-ink-200">{item.body}</p>

      {/* Кто и когда — не украшение: по анонсу видно, свежая это новость или
          та, что висит с прошлого месяца. */}
      <p className="mt-3 text-xs text-ink-500">
        {[formatDate(item.publishedAt ?? item.createdAt), item.author?.username]
          .filter(Boolean)
          .join(" · ")}
      </p>

      {unseen.length > 1 && (
        <p className="mt-1 text-xs text-ink-500">
          {t("announcements.more", { count: unseen.length - 1 })}
        </p>
      )}

      <button type="button" className="btn-primary mt-5 w-full" onClick={close}>
        {t("announcements.gotIt")}
      </button>
    </Dialog>
  );
}
